
import Vue from "vue";
import Component from "vue-class-component";
import {Prop} from "vue-property-decorator";
import {BvTableFieldArray} from "bootstrap-vue";
import {formatDate} from "@/formatting/dateFormatter";
import {PracticePeriod} from "@/api-client";
import ButtonWithConfirm from "@/features/shared/components/ButtonWithConfirm.vue";

@Component({
    components: { 
        ButtonWithConfirm
    }
})
export default class PracticePeriodsTable extends Vue {
  @Prop({ required: true })
  value!: PracticePeriod[];

  @Prop()
  openModal!: (data: PracticePeriod, index:number) => void;
  
  @Prop({ default: () => true })
  periodsCanBeRemoved!: boolean;

  @Prop({ default: () => false })
  canEdit!: boolean;

  @Prop({ default: () => false })
  withNavigationalAndEngineRoomWatch!: boolean;
  
  @Prop({ default: () => false })
  withVoyageCount!: boolean;
  
  @Prop({ default: () => false })
  isCountBased!: boolean;

  @Prop({ default: () => ({}) })
  instructorNameMap!: {[key: string]: string};

  get totalPracticeDays(): number {
    return (this.value ?? []).reduce((total, current) => total + Number(current.durationInDays || 0), 0);
  }

  get totalPracticeCount(): number {
    return (this.value ?? []).length;
  }

  get totalNavigationalWatchDays(): number {
    return (this.value ?? []).reduce((total, current) => total + Number(current.navigationalWatchDays || 0), 0);
  }

  get totalEngineRoomWatchDays(): number {
    return (this.value ?? []).reduce((total, current) => total + Number(current.engineRoomWatchDays || 0), 0);
  }
  
  private removePracticePeriod(practicePeriod: PracticePeriod) {
    this.$emit('remove', practicePeriod);
  }

  get fields(): BvTableFieldArray {
    const baseFields: BvTableFieldArray = [
      { key: 'nr', label: this.$ls.Documents_Practices_Table_PracticePeriods_Label_Number, sortable: true, formatter: (_value: unknown, _key: unknown, item: PracticePeriod) => {return this.value.indexOf(item) + 1}},
      { key: 'startedOn', label: this.$ls.Documents_Practices_Table_PracticePeriods_Label_StartingDate, sortable: true, formatter: (_dateString: string, _key: string, item: PracticePeriod) => formatDate(item.startedOn)  },
      { key: 'endedOn', label: this.$ls.Documents_Practices_Table_PracticePeriods_Label_EndingDate, sortable: true, formatter: (_dateString: string, _key: string, item: PracticePeriod) => formatDate(item.endedOn) },
    ];

    // Only show duration column if not count-based
    if (!this.isCountBased) {
      baseFields.push(
        { key: 'durationInDays', label: this.$ls.Documents_Practices_Table_PracticePeriods_Label_Duration, sortable: false }
      );
    }

    if (this.withNavigationalAndEngineRoomWatch) {
      baseFields.push(
        { key: 'navigationalWatchDays', label: this.$ls.Documents_Practices_Table_PracticePeriods_Label_NavigationalWatchDays, sortable: false },
        { key: 'engineRoomWatchDays', label: this.$ls.Documents_Practices_Table_PracticePeriods_Label_EngineRoomWatchDays, sortable: false }
      );
    }

    if (this.withVoyageCount) {
      baseFields.push(
        { key: 'voyageCount', label: 'Reiside arv', sortable: false }
      );
    }

    if (this.isCountBased) {
      baseFields.push(
        { key: 'inOutFlag', label: this.$ls.Documents_Practices_Table_PracticePeriods_Label_InOutFlag, sortable: false, formatter: (value: boolean) => value ? this.$ls.Documents_Practices_InOutFlag_In : this.$ls.Documents_Practices_InOutFlag_Out },
        { key: 'instructorFullName', label: this.$ls.Documents_Practices_Table_PracticePeriods_Label_Instructor, sortable: false, formatter: (_value: string, _key: string, item: PracticePeriod) => {
            // First try to use instructorFullName from backend
            if (item.instructorFullName) {
              return item.instructorFullName;
            }
            // Fall back to instructorId lookup in map (for edit mode)
            if (item.instructorId && this.instructorNameMap[item.instructorId]) {
              return this.instructorNameMap[item.instructorId];
            }
            return '-';
          }
        }
      );
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    baseFields.push({ key: 'actions', label: '', sortable: false, tdClass: 'table-actions'} as any);
    
    return baseFields;
  }
}
